import { Link, Typography, TypographyProps } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { get } from 'lodash';
import { useMemo } from 'react';
import { RaRecord, useRecordContext, useTranslate } from 'react-admin';

import { BoLinkPreviews, boLinkPreviews } from '@Common/types/bo/links';

import { ExternalLink } from '@sh4res/bo-common/components/ExternalLink';
import { isBoLink } from '@sh4res/bo-common/utils/env';

interface LinkFieldProps extends TypographyProps {
  source: string;
  target?: string;
  rel?: string;
  hrefSource?: string;
  plain?: boolean;
  record?: Partial<RaRecord>;
  emptyText?: string;
}

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.secondary.main,
  },
  plain: {
    textDecoration: 'none',
  },
}));

/**
 * Wrapper around TextField to display links with custom href
 */
export function LinkField(props: LinkFieldProps) {
  const classes = useStyles();
  const record = useRecordContext<Partial<RaRecord>>(props);
  const translate = useTranslate();
  const { rel, target, source, hrefSource, onClick, plain, emptyText, ...typographyProps } = props;

  const href = get(record, hrefSource ?? source);
  const text = get(record, source);

  const parsedLink = useMemo(() => {
    try {
      const parsed = new URL(href);
      const [boPath] = parsed.hash.split('?');
      const isBo = isBoLink(parsed.host);
      return {
        isExternalLink: /^https?:$/.test(parsed.protocol) && !isBo,
        linkText:
          isBo && boLinkPreviews.find((entry) => entry === boPath)
            ? translate(`resources.bo_link_preview.${boPath as BoLinkPreviews}`)
            : text,
      };
    } catch {
      return null;
    }
  }, [href, text, translate]);

  if (!source) {
    return null;
  }

  if (!parsedLink) {
    return emptyText ?? null;
  }

  const { isExternalLink, linkText } = parsedLink;

  if (isExternalLink) {
    return (
      <ExternalLink
        href={href}
        rel={rel}
        color="secondary.main"
        onClick={onClick}
        sx={{ display: 'flex', margin: '0px' }}
        className={plain ? classes.plain : undefined}>
        <Typography className={classes.link} {...typographyProps}>
          {linkText}
        </Typography>
      </ExternalLink>
    );
  }

  return (
    <Link
      href={href}
      target={target}
      rel={rel}
      onClick={onClick}
      sx={{ display: 'flex', margin: '0px' }}
      className={plain ? classes.plain : undefined}>
      <Typography className={classes.link} {...typographyProps}>
        {linkText}
      </Typography>
    </Link>
  );
}

LinkField.defaultProps = {
  target: '_blank',
  rel: 'noreferrer',
};
